import { DOCUMENT } from '@angular/common';
import { Injectable, REQUEST, inject } from '@angular/core';

import { Observable, map, of } from 'rxjs';

import { TENANT_REPOSITORY } from '@core/data/tenant/tenant.repository';
import { baseDomainOf, subdomainOf } from '@core/utils/host-domain';

import { environment } from '../../../environments/environment';
import { TenantScopeService } from './tenant-scope.service';

/**
 * Resolves which tenant a request belongs to from its host (HLD SM-02).
 *
 * A company that pays for its own site is reachable at `{subdomain}` in front
 * of the marketplace's own base domain; every other host is the shared
 * marketplace. "Brijj Finds" is the product name, not a domain - the real one
 * comes from `marketplaceWebUrl` per environment, which is why `baseDomain` is
 * published here rather than written out anywhere in the UI.
 *
 * The subdomain is read from the request `Host` on the server and from
 * `location` in the browser, so the same tenant resolves on both sides of
 * hydration and the branded markup is already in the delivered HTML.
 */
@Injectable({ providedIn: 'root' })
export class TenantHostService {
  private readonly request = inject(REQUEST, { optional: true });
  private readonly document = inject(DOCUMENT);
  private readonly tenants = inject(TENANT_REPOSITORY);
  private readonly scope = inject(TenantScopeService);

  /** The host this render is serving, lowercased and without a port. */
  readonly hostname = this.readHostname();

  private readonly marketplaceHostname = new URL(environment.marketplaceWebUrl).hostname.toLowerCase();

  /**
   * The domain tenant own-site subdomains hang off. The single source for any
   * UI that has to show a tenant's address, so copy never hard-codes a domain
   * the environment does not actually serve.
   */
  readonly baseDomain = baseDomainOf(this.hostname, this.marketplaceHostname);

  /** The tenant label in front of the base domain, or null on the marketplace host. */
  readonly subdomain = subdomainOf(this.hostname, this.marketplaceHostname);

  /**
   * Bootstrap hook (wired through `provideAppInitializer`): resolves the host
   * tenant and enters a sticky scope before the first render, so the theme is
   * applied in the server-rendered HTML rather than after hydration.
   *
   * Only companies that pay for their own site claim a host. An unknown or
   * unpaid subdomain silently falls through to the marketplace rather than
   * 404ing, so a mis-pointed DNS record degrades instead of breaking.
   */
  resolveHostTenant(): Observable<void> {
    const subdomain = this.subdomain;
    if (subdomain === null) return of(undefined);

    return this.tenants.getBySubdomain(subdomain).pipe(
      map(tenant => {
        if (tenant?.ownSiteEnabled) this.scope.enterHostTenant(tenant);
      })
    );
  }

  private readHostname(): string {
    // The server has no `location`, and the browser has no REQUEST - one of
    // the two always answers.
    if (this.request) {
      return new URL(this.request.url).hostname.toLowerCase();
    }
    return (this.document.location?.hostname ?? '').toLowerCase();
  }
}
